import { createSlice } from '@reduxjs/toolkit';
import addRow from './rows/add';
import processGridField from './processGridField';
import { IDataGridContext, IDataGridSlice, TGridPaylodAction } from './types';
import { finishLoading, processActions, putItLoading } from './actionsUtils';
import deleteRow from './rows/delete';
import down from './rows/down';
import up from './rows/up';
import gotoPage from './rows/gotoPage';
import clearPage from './rows/clearPage';
import clearGrid from './rows/clearGrid';
import refresh from './rows/refresh';
import restoreFocusAfterReload from './rows/restoreFocusAfterEvent';
import getReduxActions from '../selection/getReduxActions';

const dataGridSlice = createSlice({
  initialState: {} as Record<string, IDataGridSlice>,
  name: 'dataGridSlice',
  reducers: {
    init(
      state,
      {
        payload: { id, context },
      }: TGridPaylodAction<{ context: IDataGridContext }>,
    ) {
      state[id] = processGridField('init', state[id], context, { init: true });
      restoreFocusAfterReload(state, context, id);

      if (state[id].columns) {
        state[id].nonAdditionalColumnsCount = state[id].columns.length;
      }
    },
    toggleColumnVisibility(
      state,
      {
        payload: { fieldName, isShown, id },
      }: TGridPaylodAction<{
        fieldName: string;
        isShown?: boolean;
      }>,
    ) {
      const s = state[id];
      const columnIndex = s.columns.findIndex(
        (current) =>
          current.attName === fieldName || current.name === fieldName,
      );
      const isColumnHidden = s.columns[columnIndex].isHidden;
      if (isShown === undefined)
        s.columns[columnIndex].isHidden = !isColumnHidden;
      else s.columns[columnIndex].isHidden = !isShown;
    },
    toggleDeleteButton(
      state,
      {
        payload: { id, index, isShown },
      }: TGridPaylodAction<{ index: number; isShown?: boolean }>,
    ) {
      const s = state[id];
      const isCurrentlyShown =
        isShown === undefined ? s.showDeleteButtons[index] : !isShown;

      if (isCurrentlyShown) s.showDeleteButtons[index] = false;
      else s.showDeleteButtons[index] = true;

      s.showDeleteButtonsCount = Object.values(s.showDeleteButtons).filter(
        (current) => current === true,
      ).length;
    },
    toggleEditFormButton(
      state,
      {
        payload: { id, index, isShown },
      }: TGridPaylodAction<{ index: number; isShown?: boolean }>,
    ) {
      const s = state[id];
      const isCurrentlyShown =
        isShown === undefined ? s.showEditFormButtons[index] : !isShown;

      if (isCurrentlyShown) s.showEditFormButtons[index] = false;
      else s.showEditFormButtons[index] = true;

      s.showEditFormButtonsCount = Object.values(s.showEditFormButtons).filter(
        (current) => current === true,
      ).length;
    },
    toggleInfoButton(
      state,
      {
        payload: { id, isShown },
      }: TGridPaylodAction<{ id: string; isShown?: boolean }>,
    ) {
      const s = state[id];
      s.context.grid.field.properties.hideDocInfoButton =
        (isShown === undefined &&
          s.context.grid.field.properties.hideDocInfoButton === 'F') ||
        isShown
          ? 'T'
          : 'F';
    },
    update(
      state,
      {
        payload: { id, ...payload },
      }: TGridPaylodAction<Partial<IDataGridSlice>>,
    ) {
      state[id] = { ...state[id], ...payload };

      if (payload.columns) {
        state[id].nonAdditionalColumnsCount = payload.columns.length;
      }
    },

    // Rows selection
    ...getReduxActions(),
  },
  extraReducers: (builder) => {
    builder.addCase(addRow.pending, putItLoading);
    builder.addCase(addRow.fulfilled, processActions);
    builder.addCase(addRow.rejected, finishLoading);
    builder.addCase(clearGrid.pending, putItLoading);
    builder.addCase(clearGrid.fulfilled, processActions);
    builder.addCase(clearGrid.rejected, finishLoading);
    builder.addCase(clearPage.pending, putItLoading);
    builder.addCase(clearPage.fulfilled, processActions);
    builder.addCase(clearPage.rejected, finishLoading);
    builder.addCase(deleteRow.pending, putItLoading);
    builder.addCase(deleteRow.fulfilled, processActions);
    builder.addCase(deleteRow.rejected, finishLoading);
    builder.addCase(down.pending, putItLoading);
    builder.addCase(down.fulfilled, processActions);
    builder.addCase(down.rejected, finishLoading);
    builder.addCase(gotoPage.pending, putItLoading);
    builder.addCase(gotoPage.fulfilled, processActions);
    builder.addCase(gotoPage.rejected, finishLoading);
    builder.addCase(refresh.pending, putItLoading);
    builder.addCase(refresh.fulfilled, processActions);
    builder.addCase(refresh.rejected, finishLoading);
    builder.addCase(up.pending, putItLoading);
    builder.addCase(up.fulfilled, processActions);
    builder.addCase(up.rejected, finishLoading);
  },
});

export const dataGridActions = {
  ...dataGridSlice.actions,
  addRow,
  clearGrid,
  clearPage,
  deleteRow,
  down,
  gotoPage,
  refresh,
  up,
};

export default dataGridSlice.reducer;
